import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext(null);
const EMPTY = { user: null, workspace: null, org: null };

export function AuthProvider({ children }) {
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const authEpoch = useRef(0), latestRequest = useRef(0);

  const checkAuth = useCallback(async () => {
    const request = ++latestRequest.current, epoch = authEpoch.current;
    const current = () => request === latestRequest.current && epoch === authEpoch.current;
    setChecking(true);
    setVerificationError(null);
    try {
      const { data } = await api.get("/auth/me");
      if (!current()) return null;
      if (!data?.user || !(data.user.id || data.user.user_id)) throw new Error("Invalid account response");
      setState(data);
      return data;
    } catch (error) {
      if (current()) {
        const status = error?.response?.status;
        setVerificationError({kind: status === 401 || status === 403 ? "denied" : "unavailable", retryable: !status || status >= 500});
        setState(EMPTY);
      }
      return null;
    } finally {
      if (current()) { setLoading(false); setChecking(false); }
    }
  }, []);

  useEffect(() => {
    let active = true;
    // Subscribe before bootstrap. A stale empty session must not finish loading
    // while a newer sign-in is still being verified by the server.
    const bootstrapEpoch = authEpoch.current;
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        authEpoch.current += 1; latestRequest.current += 1;
        setState(EMPTY); setLoading(false); setChecking(false);
        setVerificationError({kind: "signed_out", retryable: false});
      } else if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        authEpoch.current += 1;
        setChecking(true); setVerificationError(null);
        setTimeout(() => { if (active) checkAuth(); }, 0);
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      if (!active || authEpoch.current !== bootstrapEpoch) return;
      if (data.session) checkAuth();
      else setLoading(false);
    }).catch(() => {
      if (active && authEpoch.current === bootstrapEpoch) setLoading(false);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      setState(EMPTY);
    }
  }, []);

  const value = { ...state, loading, checking, verificationError, setAuth: (d) => { setState(d); setLoading(false); }, refresh: checkAuth, logout, isAuthenticated: !!state.user };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
